import { useEffect, useRef } from "react";
import { useBuilderToast } from "../../../components/builder/BuilderToast";
import { useT } from "../../../i18n";

interface BuilderMilestoneAnnouncerProps {
	/** Rising edge = every rule check just came back clear. */
	rulesCelebrating: boolean;
	/**
	 * Rising edge = the design just became complete AND the summary rail that
	 * normally carries that confirmation is not on screen. The rail is not
	 * rendered on the design workspace, which is exactly where deck, postal
	 * code and openings are edited - without this the moment would be silent
	 * every time it actually happens.
	 */
	completenessCelebrating?: boolean;
}

/**
 * Renders nothing; turns a milestone's rising edge into one quiet toast.
 *
 * It exists as a component (rather than a call inside BuilderPage) because
 * `useBuilderToast` only resolves for children of `BuilderToastProvider`, and
 * BuilderPage renders that provider itself. Reuses the existing toast surface -
 * no second notification system.
 */
export function BuilderMilestoneAnnouncer({
	rulesCelebrating,
	completenessCelebrating = false,
}: BuilderMilestoneAnnouncerProps) {
	const { show } = useBuilderToast();
	const t = useT();
	// Once per session, not once per rising edge. `completenessCelebrating` is
	// gated on the active workspace by the caller, so tabbing away and back
	// inside the celebration window would otherwise re-open the gate and toast
	// again on every return. The hook's own latch cannot see that gate.
	const announcedRulesRef = useRef(false);
	const announcedCompletenessRef = useRef(false);

	useEffect(() => {
		if (!rulesCelebrating || announcedRulesRef.current) return;
		announcedRulesRef.current = true;
		show(t.milestone_rulesAllClear, "add");
	}, [rulesCelebrating, show, t]);

	useEffect(() => {
		if (!completenessCelebrating || announcedCompletenessRef.current) return;
		announcedCompletenessRef.current = true;
		show(t.milestone_completenessNote, "add");
	}, [completenessCelebrating, show, t]);

	return null;
}
